

import React, { useState } from 'react';
import _ from 'lodash';
import cn from 'classnames';

import styles from './traveler-profile-search-criteria-form.module.css';

import ButtonDefault, {
  ButtonPrimary,
} from '../../../components/forms/Buttons';
import { Form } from '../../../components/forms/Form';
import { EmailInput, Input } from '../../../components/forms/Input';
import { InternationalPhoneInput } from '../../../components/forms/InternationalPhoneInput';
import { Label } from '../../../components/forms/Label';
import Alert from '../../../components/alert';

import SearchCriteria from '../../../components/SearchCriteria';
import type { TravelerProfileSearchCriteria } from '../../../types/travelerProfile';

interface SearchFormProps {
  supportedSMSCountryCodes: string[];
  onSearch: (searchCriteria: TravelerProfileSearchCriteria) => void;
  onClear: () => void;
  searchError?: string | null;
}

const SearchForm: React.FC<SearchFormProps> = ({
  supportedSMSCountryCodes,
  onSearch,
  onClear,
  searchError,
}) => {
  const [travelerKeyInput, updateTravelerKeyInput] = useState<string>('');
  const [emailInput, updateEmailInput] = useState<string>('');
  const [emailInputErrorMessage, updateEmailInputErrorMessage] = useState<string | undefined>(undefined);
  const [phoneNumberE164Input, updatePhoneNumberE164Input] = useState<string>('');
  const [phoneDialCodeInput, updatePhoneDialCodeInput] = useState<string>('');

  const shouldShowPhoneSearch = supportedSMSCountryCodes.length !== 0;

  const isPhoneNumberE164InputEmpty = _.isEmpty(phoneNumberE164Input) || phoneNumberE164Input === `+${phoneDialCodeInput}`;

  const isFormEmpty = _.isEmpty(travelerKeyInput) && _.isEmpty(emailInput) && isPhoneNumberE164InputEmpty;

  const hasEmailInputError = !_.isEmpty(emailInputErrorMessage);

  const hasFormErrors = hasEmailInputError;

  const isSearchDisabled = isFormEmpty || hasFormErrors;
  const isClearDisabled = isFormEmpty;

  const handleOnClear = (): void => {
    updateTravelerKeyInput('');
    updateEmailInput('');
    updateEmailInputErrorMessage(undefined);
    updatePhoneNumberE164Input('');
    onClear();
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const profileSearchCriteria: TravelerProfileSearchCriteria = {
      ...(travelerKeyInput && { travelerKey: travelerKeyInput }),
      ...(emailInput && { email: emailInput }),
      ...(!isPhoneNumberE164InputEmpty && { phoneNumberE164: phoneNumberE164Input }),
    };
    onSearch(profileSearchCriteria);
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      {searchError && <Alert type="danger" message={searchError} />}
      <div className={cn(styles.inputElementGroupWrapper)}>
        <div className={cn(styles.inputElementWrapper)}>
          <Label htmlFor="travelerKeyId" text="Traveler Key" />
          <Input
            name="travelerKey"
            id="travelerKeyId"
            placeholder="john@doe.com"
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              updateTravelerKeyInput(e.currentTarget.value);
            }}
            value={travelerKeyInput}
          />
        </div>

        <div className={cn(styles.inputElementWrapper)}>
          <Label htmlFor="emailId" text="Email Address" />
          <EmailInput
            name="email"
            id="emailId"
            placeholder="john@doe.com"
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              updateEmailInput(e.currentTarget.value);
            }}
            onError={(errorMessage: string | undefined): void => {
              updateEmailInputErrorMessage(errorMessage);
            }}
            value={emailInput}
          />
        </div>

        {shouldShowPhoneSearch && (
          <div className={cn(styles.inputElementWrapper)}>
            <Label htmlFor="phone" text="Phone" />
            <InternationalPhoneInput
              onlyCountries={supportedSMSCountryCodes}
              phoneNumber={phoneNumberE164Input}
              onChange={(phoneNumberE164, country) => {
                updatePhoneDialCodeInput(country.dialCode);
                updatePhoneNumberE164Input(phoneNumberE164);
              }}
              onEnterKeyPress={(e: React.KeyboardEvent<HTMLInputElement>): void => {
                if (e.key === 'Enter') {
                  handleFormSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
                }
              }}
            />
          </div>
        )}
      </div>

      <div className={cn(styles.buttonGroupWrapper)}>
        <FormButton
          id="clearButton"
          isDefault
          onClick={handleOnClear}
          label="CLEAR"
          disabled={isClearDisabled}
        />
        <FormButton
          id="searchButton"
          label="SEARCH"
          disabled={isSearchDisabled}
          type="submit"
        />
      </div>
    </Form>
  );
};

interface FormButtonProps {
  id: string;
  isDefault?: boolean;
  label: string;
  onClick?: () => void;
  disabled: boolean;
  type?: 'button' | 'submit';
}

const FormButton: React.FC<FormButtonProps> = ({
  id,
  isDefault,
  label,
  onClick,
  disabled,
  type = 'button',
}) =>
  isDefault ? (
    <div className={cn(styles.buttonWrapper)}>
      <ButtonDefault id={id} onClick={onClick} disabled={disabled} type={type}>
        {label}
      </ButtonDefault>
    </div>
  ) : (
    <div className={cn(styles.buttonWrapper)}>
      <ButtonPrimary id={id} onClick={onClick} disabled={disabled} type={type}>
        {label}
      </ButtonPrimary>
    </div>
  );

interface TravelerProfileSearchCriteriaFormProps extends SearchFormProps {}

export const TravelerProfileSearchCriteriaForm: React.FC<TravelerProfileSearchCriteriaFormProps> = (props) => {
  return (
    <SearchCriteria id="travelerProfileSearchCriteria" title="PROFILE SEARCH CRITERIA">
      <SearchForm {...props} />
    </SearchCriteria>
  );
};
